import React, { FC } from 'react';
import s from './TemplateMediaContainer.scss';
import cs from 'classnames';
import { EditButton } from '../EditButton/EditButton';
import { isBlankTemplate, Template } from '../../domain/entities/template';
import { ViewButton } from '../ViewButton/ViewButton';
import { useTranslation } from '@wix/wix-i18n-config';
import { useViewUrl } from '../../hooks/useViewUrl';
import { useIsMounted } from '../../hooks/useIsMounted';
import { useBiLogger } from '../../contexts/BILoggerContext';
import { useInitialState } from '../../contexts/InitialStateContext';
import { usePreviewTemplate } from '../../contexts/PreviewTemplateContext';
import { templateMediaContainerDataHooks } from './TemplateMediaContainerDataHooks';

export type TemplateMediaContainerProps = {
  children?: React.ReactNode;
  template: Template;
  index?: number;
  isLoading?: boolean;
  categorySlug?: string;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
};

export const TemplateMediaContainer: FC<TemplateMediaContainerProps> = ({
  children,
  template,
  index,
  categorySlug,
  onMouseEnter,
  onMouseLeave,
  isLoading,
}) => {
  const { t } = useTranslation();
  const viewUrl = useViewUrl(template);
  const isMounted = useIsMounted();
  const biLogger = useBiLogger();
  const { isMobile } = useInitialState();
  const { setPreviewTemplate } = usePreviewTemplate();

  const onViewTemplateClick = () => {
    if (isMobile) {
      setPreviewTemplate(template);
    }
  };

  const onViewClick = () => {
    biLogger.reportViewTemplate({ ...template, index, categorySlug });
  };

  return (
    <div
      onClick={onViewTemplateClick}
      className={s.templateMediaContainer}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div
        className={cs(s.templateMediaLoadingContainer, {
          [s.animationOverMedia]: isMounted,
          [s.loading]: isLoading,
        })}
      />
      {children}
      {!isMobile && (
        <a
          target="_blank"
          className={cs(s.viewLink, s.hideOnDesktop)}
          href={viewUrl}
          rel="noreferrer"
          onClick={onViewClick}
        >
          {t('app.templates.grid.view.button')}
        </a>
      )}
      <div className={s.buttonsOverlay}>
        <div className={s.buttonsContainer}>
          <EditButton
            skin="light"
            template={template}
            categorySlug={categorySlug}
            index={index}
            dataHook={templateMediaContainerDataHooks.editButton({
              key: template.templateId,
            })}
            className={s.editButton}
          />
          {!isBlankTemplate(template) && (
            <ViewButton
              skin="light"
              template={template}
              categorySlug={categorySlug}
              index={index}
              dataHook={templateMediaContainerDataHooks.viewButton({
                key: template.templateId,
              })}
              className={s.viewButton}
            />
          )}
        </div>
      </div>
    </div>
  );
};
